/**
 * RWBR — WHO IS AT THE KEYBOARD.
 *
 * Howard, 17 Aug 2026:
 *
 *   "They need to be able to register with email and password and save their
 *    leagues and reload them when they come back."
 *
 * That is the entire scope of this module. Email and password, nothing else.
 * No magic links, no OAuth, no phone. A racing simulator does not need six ways
 * in, and every extra one is another flow to get wrong.
 *
 * AUTH GATES SAVING, NOT PLAYING. Nothing in this file is imported by the
 * engine, by `App.tsx`, or by any screen that runs a race. A visitor who never
 * signs in loses exactly one capability — coming back to the same career — and
 * keeps every other one. There is no login wall, and adding one later would be
 * a decision to argue about on its own merits rather than a side effect of this.
 *
 * WHY THERE IS NO USER TABLE HERE.
 *
 * `auth.users` already exists in the Supabase project. It hashes with bcrypt,
 * it handles confirmation and reset, it is audited by people who do that for a
 * living. Writing our own would be a downgrade wearing a schema. This module is
 * a thin, honest layer over `supabase.auth` — and the honesty is the point,
 * because the raw errors it returns are written for developers, not drivers.
 *
 * WHY THE ERROR MAPPING IS A PURE FUNCTION.
 *
 * `describeAuthFailure` takes an error-shaped value and returns a code and a
 * sentence. It touches no network and no client, so every branch of it is
 * tested in `auth.test.ts` against the real codes GoTrue emits. That matters
 * more than it sounds: the difference between "wrong password" and "that email
 * is already registered" and "you appear to be offline" is the difference
 * between a user who tries again and a user who leaves. A single generic
 * "something went wrong" would make all three indistinguishable, and this
 * project does not ship those.
 */

import type { AuthChangeEvent, Session, SupabaseClient, User } from '@supabase/supabase-js';
import { supabase as defaultClient } from './supabase.ts';

// ---------------------------------------------------------------------------
// What a caller gets back
// ---------------------------------------------------------------------------

/** The signed-in identity, reduced to the two facts the interface actually shows. */
export interface Account {
  id: string;
  email: string;
}

/**
 * Every distinct way this can fail, named. The interface switches on `code`
 * when it wants to do something different (offer "sign in instead" after
 * `email-already-registered`, for example); it shows `message` otherwise.
 */
export type AuthFailureCode =
  | 'invalid-email'
  | 'weak-password'
  | 'missing-field'
  | 'invalid-credentials'
  | 'email-already-registered'
  | 'email-not-confirmed'
  | 'rate-limited'
  | 'signup-disabled'
  | 'user-banned'
  | 'session-expired'
  | 'offline'
  | 'server-error'
  | 'unknown';

export interface AuthFailure {
  ok: false;
  code: AuthFailureCode;
  /** Written for the person at the keyboard. Complete sentence, no jargon. */
  message: string;
  /** The raw text the server sent, kept so a bug report can carry it. */
  detail?: string;
}

export interface AuthSuccess {
  ok: true;
  /** Null after a sign-up that still needs email confirmation — see `note`. */
  account: Account | null;
  /**
   * Present only when something true happened that is neither success nor
   * failure and the user must be told: "we sent you a confirmation email".
   */
  note?: string;
}

export type AuthResult = AuthSuccess | AuthFailure;

// ---------------------------------------------------------------------------
// WHO OWNS THIS INSTALLATION — a UI affordance, and NOT a security boundary
// ---------------------------------------------------------------------------

/**
 * THE OWNER'S EMAIL. One constant, one place, and the only identity check in
 * the interface.
 *
 * Howard, 17 Aug 2026:
 *
 *   "users other than me (Howard@roundtablerealty.com) cannot see the art
 *    gallery as they dont have edit options like that. Please remove that from
 *    the dashboard for anyone else."
 *
 * ⚠ THIS CONSTANT IS STILL ONLY A UI AFFORDANCE. All it does is stop a tab
 * appearing for someone who has no reason to use it. Nothing a browser decides
 * can protect an endpoint, because the browser is the thing under the
 * attacker's control — so nobody should read this constant as though it were a
 * security boundary, however convincing the hidden tab looks.
 *
 * THE REAL GATE IS ELSEWHERE, AND IT NOW EXISTS. `worker/owner.ts` holds the
 * server-side copy of this address. Every art WRITE route named in `ART_WRITE`
 * in `worker/index.ts` — upload, save, rename, delete — requires an
 * `Authorization: Bearer <supabase access token>` header, resolves that token
 * to a real user by asking Supabase's `/auth/v1/user` (never by decoding the
 * JWT, which authenticates nobody), and refuses anything whose VERIFIED email is
 * not this address: 401 when there is no usable token, 403 when the token is
 * good and the person is somebody else. `src/lib/artStore.ts` attaches the token
 * and surfaces those two answers differently.
 *
 * ⚠ THERE ARE THEREFORE TWO COPIES OF THIS ADDRESS. The other is `OWNER_EMAIL`
 * in `worker/owner.ts`, which cannot import this file — the Worker is a separate
 * deployment target. Change one and you must change the other;
 * `worker/owner.test.ts` imports both and fails if they ever differ.
 *
 * READS ARE STILL PUBLIC AND ARE MEANT TO BE. `loadLibrary` powers the
 * photographs on every player's race screen. Gating reads would blank the race
 * for everyone who is not Howard, which is not a security fix.
 *
 * NO PASSWORD IS INVOLVED, HERE OR ANYWHERE. The check is on the email of the
 * signed-in Supabase session and nothing else. A credential in the bundle would
 * be readable by everyone who loads the page, which is the opposite of a gate.
 */
export const OWNER_EMAIL = 'howard@roundtablerealty.com';

/**
 * Pure. Is the signed-in account the owner's?
 *
 * Compared case-insensitively and trimmed: an address typed as
 * "Howard@Roundtablerealty.com " is the same mailbox, and a gate that told the
 * owner he was not himself because of a capital H would be a bug he could not
 * diagnose. Signed out — `null` — is never the owner, which is what makes a
 * visitor with no account the default state rather than a special case.
 */
export function isOwner(account: Pick<Account, 'email'> | null | undefined): boolean {
  const email = account?.email;
  if (typeof email !== 'string') return false;
  return email.trim().toLowerCase() === OWNER_EMAIL;
}

// ---------------------------------------------------------------------------
// Local validation — refuse before the round trip, but never lie about limits
// ---------------------------------------------------------------------------

/**
 * GoTrue's own default floor is 6 characters. We ask for 8.
 *
 * That is OUR policy, not a claim about the server, and it is stated that way
 * in the message. If the project is configured to demand more than 8 — or to
 * demand character classes — the server refuses, and `describeAuthFailure`
 * passes the server's own wording through verbatim rather than substituting a
 * guess. Client-side rules exist to save a round trip on the obvious cases,
 * never to invent a rule the server does not have.
 */
export const MIN_PASSWORD_LENGTH = 8;

/**
 * The loosest defensible email check: something, an @, something with a dot.
 *
 * Deliberately not RFC 5322. A stricter regex here would reject valid
 * addresses, and rejecting a real address is a far worse failure than passing a
 * typo to the server, which validates properly and whose verdict we surface.
 */
const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Pure. Returns null when the credentials are worth sending. */
export function validateCredentials(email: string, password: string): AuthFailure | null {
  const trimmed = email.trim();
  if (!trimmed) {
    return { ok: false, code: 'missing-field', message: 'Enter your email address.' };
  }
  if (!password) {
    return { ok: false, code: 'missing-field', message: 'Enter your password.' };
  }
  if (!EMAIL_SHAPE.test(trimmed)) {
    return {
      ok: false,
      code: 'invalid-email',
      message: `"${trimmed}" does not look like an email address — it needs an @ and a domain.`,
    };
  }
  return null;
}

/** Pure. Sign-up only: an existing account's password is whatever it already is. */
export function validateNewPassword(password: string): AuthFailure | null {
  if (password.length < MIN_PASSWORD_LENGTH) {
    return {
      ok: false,
      code: 'weak-password',
      message: `Choose a password of at least ${MIN_PASSWORD_LENGTH} characters. Yours is ${password.length}.`,
    };
  }
  return null;
}

// ---------------------------------------------------------------------------
// Error mapping — pure, and the most-tested thing in this file
// ---------------------------------------------------------------------------

/** The parts of a GoTrue error we read. Loose on purpose: this is untrusted input. */
interface ErrorLike {
  message?: unknown;
  code?: unknown;
  status?: unknown;
  name?: unknown;
}

/**
 * Turn anything the client threw or returned into a named, explainable failure.
 *
 * The codes come from `@supabase/auth-js`'s own `ErrorCode` union, so this is
 * matching on what GoTrue actually emits rather than on English substrings that
 * change when someone edits a template. `message` is checked only as a fallback
 * for older responses that predate `code`, and the raw text is always kept in
 * `detail` so nothing the server said is thrown away.
 */
export function describeAuthFailure(raw: unknown): AuthFailure {
  const e = (raw ?? {}) as ErrorLike;
  const code = typeof e.code === 'string' ? e.code : '';
  const message = typeof e.message === 'string' ? e.message : '';
  const status = typeof e.status === 'number' ? e.status : undefined;
  const detail = message || undefined;
  const lower = message.toLowerCase();

  // ---- the network never got there ---------------------------------------
  //
  // `fetch` rejects with a TypeError and no status when the request never left
  // the machine. Saying "wrong password" here would be a lie, and it is the lie
  // users most often act on — they change a password that was never wrong.
  if (
    status === undefined &&
    (e.name === 'TypeError' ||
      code === 'request_timeout' ||
      lower.includes('failed to fetch') ||
      lower.includes('networkerror') ||
      lower.includes('network request failed') ||
      lower.includes('load failed'))
  ) {
    return {
      ok: false,
      code: 'offline',
      message: 'Could not reach the server. Check your connection and try again — nothing was sent.',
      detail,
    };
  }

  switch (code) {
    case 'invalid_credentials':
      return {
        ok: false,
        code: 'invalid-credentials',
        message: 'That email and password do not match an account. Check both, or register if you are new.',
        detail,
      };

    case 'email_exists':
    case 'user_already_exists':
    case 'identity_already_exists':
      return {
        ok: false,
        code: 'email-already-registered',
        message: 'That email is already registered. Sign in instead.',
        detail,
      };

    case 'email_not_confirmed':
      return {
        ok: false,
        code: 'email-not-confirmed',
        message: 'This account has not been confirmed yet. Open the confirmation email we sent, then sign in.',
        detail,
      };

    case 'weak_password':
      // The server knows the real rule; say what it said rather than guessing.
      return {
        ok: false,
        code: 'weak-password',
        message: message || `That password was refused. Try a longer one — at least ${MIN_PASSWORD_LENGTH} characters.`,
        detail,
      };

    case 'email_address_invalid':
    case 'validation_failed':
      return {
        ok: false,
        code: 'invalid-email',
        message: message || 'That email address was refused. Check it for typos.',
        detail,
      };

    case 'over_request_rate_limit':
    case 'over_email_send_rate_limit':
      return {
        ok: false,
        code: 'rate-limited',
        message: 'Too many attempts in a short time. Wait a minute and try again.',
        detail,
      };

    case 'signup_disabled':
    case 'email_provider_disabled':
      return {
        ok: false,
        code: 'signup-disabled',
        message: 'New registrations are turned off on this server. Nothing you did was wrong.',
        detail,
      };

    case 'user_banned':
      return {
        ok: false,
        code: 'user-banned',
        message: 'This account is suspended and cannot sign in.',
        detail,
      };

    case 'session_expired':
    case 'session_not_found':
    case 'refresh_token_not_found':
    case 'refresh_token_already_used':
    case 'bad_jwt':
      return {
        ok: false,
        code: 'session-expired',
        message: 'Your session expired. Sign in again to keep saving.',
        detail,
      };
  }

  // ---- fallbacks for responses with no `code` ----------------------------
  if (status === 429) {
    return {
      ok: false,
      code: 'rate-limited',
      message: 'Too many attempts in a short time. Wait a minute and try again.',
      detail,
    };
  }
  if (status !== undefined && status >= 500) {
    return {
      ok: false,
      code: 'server-error',
      message: `The authentication server returned an error (${status}). This is not something you can fix — try again shortly.`,
      detail,
    };
  }
  if (lower.includes('invalid login credentials')) {
    return {
      ok: false,
      code: 'invalid-credentials',
      message: 'That email and password do not match an account. Check both, or register if you are new.',
      detail,
    };
  }
  if (lower.includes('already registered')) {
    return {
      ok: false,
      code: 'email-already-registered',
      message: 'That email is already registered. Sign in instead.',
      detail,
    };
  }

  // Nothing recognised. Say so plainly and hand over what the server said —
  // an unexplained failure the user can quote is worth more than a friendly
  // one they cannot.
  return {
    ok: false,
    code: 'unknown',
    message: message
      ? `Sign-in failed: ${message}`
      : 'Sign-in failed, and the server did not say why. Try again; if it keeps happening, this is a bug worth reporting.',
    detail,
  };
}

// ---------------------------------------------------------------------------
// Reading a sign-up response — the case that looks like success and is not
// ---------------------------------------------------------------------------

/** The shape of `supabase.auth.signUp`'s `data`, reduced to what we read. */
export interface SignUpData {
  user: Pick<User, 'id' | 'email'> & { identities?: unknown[] | null } | null;
  session: Pick<Session, 'access_token'> | null;
}

/**
 * Pure. Decide what a sign-up response actually MEANS.
 *
 * There are three outcomes hiding behind one HTTP 200, and two of them are not
 * "you are registered":
 *
 *  1. Registered and signed in — confirmations are off, `session` is present.
 *
 *  2. Registered, must confirm — `user` present, `session` null. Telling this
 *     person "you're in" and then failing every save would be the worst of the
 *     three outcomes, so it returns success WITH a note and a null account.
 *
 *  3. ALREADY REGISTERED, dressed as success. When email confirmation is on,
 *     GoTrue deliberately does not reveal whether an address exists — signing
 *     up with a taken address returns 200 with a user whose `identities` array
 *     is EMPTY. This is an anti-enumeration measure and it is documented
 *     behaviour, not a bug. If we did not check for it, a returning user who
 *     forgot they had an account would be told registration succeeded, never
 *     receive an email, and never be able to sign in.
 *
 * We choose to tell the user their address is already registered. That does
 * give up the anti-enumeration property GoTrue was protecting. It is a
 * deliberate trade: this is a hobby racing simulator whose account list is not
 * sensitive, and the alternative is a dead end with no explanation — exactly
 * the "blank with an explainer taped over it" this project refuses. Anyone who
 * wants the privacy property back should change this branch to the neutral
 * "check your email" note and accept the confusion that comes with it.
 */
export function interpretSignUp(data: SignUpData | null): AuthResult {
  const user = data?.user ?? null;

  if (user && Array.isArray(user.identities) && user.identities.length === 0) {
    return {
      ok: false,
      code: 'email-already-registered',
      message: 'That email is already registered. Sign in instead, or reset the password if you have forgotten it.',
    };
  }

  if (!user) {
    return {
      ok: false,
      code: 'unknown',
      message: 'The server accepted the registration but returned no account. Try signing in; if that fails, register again.',
    };
  }

  if (!data?.session) {
    return {
      ok: true,
      account: null,
      note: `Account created for ${user.email ?? 'that address'}. Open the confirmation email we just sent, then sign in — saving is available once you are signed in.`,
    };
  }

  return { ok: true, account: toAccount(user) };
}

/** Pure. The two fields the interface shows, and nothing else. */
export function toAccount(user: { id: string; email?: string | null } | null | undefined): Account | null {
  if (!user?.id) return null;
  // An email/password account always has an email; the fallback exists so a
  // missing one shows the id rather than an empty chip that looks broken.
  return { id: user.id, email: user.email ?? '(no email on file)' };
}

// ---------------------------------------------------------------------------
// The network calls — thin, and every one of them funnels into the above
// ---------------------------------------------------------------------------

/**
 * The client is injectable for the same reason `CareerReader`'s is: so the
 * logic above can be exercised without a network. Nothing in `auth.test.ts`
 * opens a socket.
 */
export type AuthClient = Pick<SupabaseClient, 'auth'>;

export async function register(
  email: string,
  password: string,
  client: AuthClient = defaultClient,
): Promise<AuthResult> {
  const bad = validateCredentials(email, password) ?? validateNewPassword(password);
  if (bad) return bad;

  try {
    const { data, error } = await client.auth.signUp({ email: email.trim(), password });
    if (error) return describeAuthFailure(error);
    return interpretSignUp(data as SignUpData);
  } catch (e) {
    return describeAuthFailure(e);
  }
}

export async function signIn(
  email: string,
  password: string,
  client: AuthClient = defaultClient,
): Promise<AuthResult> {
  // No password-length check on sign-in. The account's password is whatever it
  // already is, and refusing to even try it because it is short would lock out
  // anyone who registered before this rule existed.
  const bad = validateCredentials(email, password);
  if (bad) return bad;

  try {
    const { data, error } = await client.auth.signInWithPassword({ email: email.trim(), password });
    if (error) return describeAuthFailure(error);
    const account = toAccount(data?.user);
    if (!account) {
      return {
        ok: false,
        code: 'unknown',
        message: 'The server signed you in but returned no account. Reload the page and try again.',
      };
    }
    return { ok: true, account };
  } catch (e) {
    return describeAuthFailure(e);
  }
}

/**
 * Sign out.
 *
 * A failure here is reported rather than swallowed, because a sign-out that
 * silently did not happen leaves someone believing they are signed out on a
 * shared machine. That is the one failure in this file with a safety
 * consequence.
 */
export async function signOut(client: AuthClient = defaultClient): Promise<AuthResult> {
  try {
    const { error } = await client.auth.signOut();
    if (error) return describeAuthFailure(error);
    return { ok: true, account: null };
  } catch (e) {
    return describeAuthFailure(e);
  }
}

/**
 * Who is signed in right now, if anyone.
 *
 * Returns `null` for "nobody", and that is not an error — a signed-out visitor
 * is the normal state of this app. Callers distinguish the two by the thrown
 * exception, which only happens when the session could not be READ (storage
 * blocked, token corrupt) as opposed to being absent.
 */
export async function currentAccount(client: AuthClient = defaultClient): Promise<Account | null> {
  const { data, error } = await client.auth.getSession();
  if (error) throw describeAuthFailure(error);
  return toAccount(data?.session?.user);
}

/**
 * Watch for sign-in and sign-out, including the ones this tab did not cause —
 * another tab signing out, or a refresh token expiring mid-session. Without
 * this the Save button would stay lit for an account that is no longer there
 * and fail at the database.
 *
 * Returns an unsubscribe function; call it from a `useEffect` cleanup.
 */
export function onAccountChange(
  handler: (account: Account | null, event: AuthChangeEvent) => void,
  client: AuthClient = defaultClient,
): () => void {
  const { data } = client.auth.onAuthStateChange((event, session) => {
    handler(toAccount(session?.user), event);
  });
  return () => data.subscription.unsubscribe();
}
